import { useQuery } from '@tanstack/react-query';
import {
  getDevicesList,
  getGatewayList,
  getParameterByEui,
  getAllParameterList,
} from '@/services/deviceService.js';
import { QUERY_KEYS } from '@/shared/constant/queryKeys.js';
import { CACHE_TIMES } from '@/shared/config/reactQuery';

const useDevice = (page = 1, pageSize = 50, eui = null, options = {}) => {
  const {
    enabled = true,
    enableLists = enabled,
    enableAllParameters = enabled,
  } = options;

  // 1. Fetch Devices List
  const {
    data: devicesList,
    isLoading: isLoadingDevices,
    error: devicesError,
    refetch: refetchDevices,
  } = useQuery({
    queryKey: QUERY_KEYS.DEVICES_LIST(page, pageSize),
    queryFn: () => getDevicesList(page, pageSize),
    enabled: enableLists,
    select: (response) => ({
      items: response.data?.devices || [],
      total: response.meta?.total || 0,
    }),
    staleTime: CACHE_TIMES.MEDIUM,
    gcTime: CACHE_TIMES.LONG,
  });

  // 2. Fetch Gateways List
  const {
    data: gatewaysList,
    isLoading: isLoadingGateways,
    error: gatewaysError,
    refetch: refetchGateways,
  } = useQuery({
    queryKey: QUERY_KEYS.GATEWAYS_LIST(page, pageSize),
    queryFn: () => getGatewayList(page, pageSize),
    enabled: enableLists,
    select: (response) => ({
      items: response.data?.gateways || [],
      total: response.meta?.total || 0,
    }),
    staleTime: CACHE_TIMES.MEDIUM,
    gcTime: CACHE_TIMES.LONG,
  });

  // 3. Fetch Parameter by EUI (Only if eui is provided)
  const {
    data: parameterData,
    isLoading: isLoadingParameter,
    error: parameterError,
    refetch: refetchParameter,
  } = useQuery({
    queryKey: QUERY_KEYS.DEVICE_PARAMETER(eui),
    queryFn: () => getParameterByEui(eui),
    enabled: enabled && !!eui,
    select: (response) => response.data || null,
    staleTime: CACHE_TIMES.MEDIUM,
    gcTime: CACHE_TIMES.LONG,
  });

  const {
    data: allParametersList,
    isLoading: isLoadingAllParameters,
    error: allParametersError,
    refetch: refetchAllParameters,
  } = useQuery({
    queryKey: QUERY_KEYS.DEVICE_PARAMETER_ALL,
    queryFn: getAllParameterList,
    enabled: enableAllParameters,
    select: (response) => response.data || [],
    staleTime: CACHE_TIMES.MEDIUM,
    gcTime: CACHE_TIMES.LONG,
  });

  return {
    // Devices
    devicesList,
    isLoadingDevices,
    devicesError,
    refetchDevices,

    // Gateways
    gatewaysList,
    isLoadingGateways,
    gatewaysError,
    refetchGateways,

    // Parameter by Eui
    parameterData,
    isLoadingParameter,
    parameterError,
    refetchParameter,

    // All Parameters
    allParametersList,
    isLoadingAllParameters,
    allParametersError,
    refetchAllParameters,
  };
};

export default useDevice;
